<template>
  <div class="tenant-data-copy">
    <div class="header-section">
      <div class="header-top">
        <div>
          <h1 class="page-title">Tenant Data Copy</h1>
          <p class="page-subtitle">
            Copy configuration and data from one tenant to another
          </p>
        </div>

        <button
          class="review-copy-btn"
          :disabled="!canCopy"
          @click="handleCopy"
        >
          Copy
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path
              d="M5 12h14M12 5l7 7-7 7"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              fill="none"
            />
          </svg>
        </button>
      </div>
    </div>

    <div class="step-section">
      <div class="step-header">STEP 1 &mdash; SELECT TENANTS</div>
      <div class="step-1-content">
        <div class="tenant-card source-card">
          <div class="badge source-badge">SOURCE TENANT</div>
          <div class="custom-select-wrapper">
            <customDropdown
              class="tenant-dropdown"
              :list="tenantsList"
              :selected="selectedSource"
              @select="selectSourceTenant"
              defaultLabel="Select source tenant..."
              :enableSearch="true"
              :infinite-scroll="true"
              :is-loading="tenantPagination.loading"
              :has-more="tenantPagination.hasMore"
              @load-more="loadMoreTenants"
              @search-change="handleTenantSearch"
            />
          </div>
        </div>

        <div class="copy-arrow-section">
          <div class="arrow-circle">
            <svg viewBox="0 0 24 24" class="arrow-icon" width="18" height="18">
              <path
                d="M5 12h14M12 5l7 7-7 7"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="none"
              />
            </svg>
          </div>
          <div class="copy-text">COPY TO</div>
        </div>

        <div class="tenant-card target-card">
          <div class="badge target-badge">TARGET TENANT</div>
          <div class="custom-select-wrapper">
            <customDropdown
              class="tenant-dropdown"
              :list="tenantsList"
              :selected="selectedTarget"
              @select="selectTargetTenant"
              defaultLabel="Select target tenant..."
              :enableSearch="true"
              :infinite-scroll="true"
              :is-loading="tenantPagination.loading"
              :has-more="tenantPagination.hasMore"
              @load-more="loadMoreTenants"
              @search-change="handleTenantSearch"
            />
          </div>
        </div>
      </div>
      <span v-if="sameTenantSelected" class="tenant-selection-warning">
        Source and target tenants cannot be the same.
      </span>
    </div>

    <transition
      name="curtain"
      @before-leave="beforeLeave"
      @leave="leave"
      @after-leave="afterLeave"
    >
      <div
        class="step-section"
        v-if="selectedSource?.id && selectedTarget?.id && !sameTenantSelected"
      >
        <div class="step-2-header-container">
          <div class="step-header">STEP 2 &mdash; FEATURES TO COPY</div>
          <div class="step-2-actions">
            <!-- Select features: for next iteration -->
            <!-- <button class="action-btn text-primary" @click="selectAll">
            Select all
          </button>
          <span class="dot-separator">&middot;</span>
          <button class="action-btn text-secondary" @click="clearAll">
            Clear
          </button> -->
            <!-- Select features: for next iteration -->

            <div class="selected-count-badge">
              {{ selectedFeaturesCount }} selected
            </div>
          </div>
        </div>

        <div class="features-list">
          <div
            v-for="(feature, index) in features"
            :key="index"
            class="feature-card"
            :class="{ 'is-selected': feature.selected }"
            @click="toggleFeature(feature)"
          >
            <!-- Select features: for next iteration -->
            <!-- <div class="feature-checkbox" :class="{ checked: feature.selected }">
            <svg
              v-if="feature.selected"
              viewBox="0 0 24 24"
              width="12"
              height="12"
            >
              <path
                d="M4 12l5 5L20 7"
                stroke="white"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="none"
              />
            </svg>
          </div> -->
            <!-- Select features: for next iteration -->

            <div
              class="feature-icon-container"
              :class="'icon-bg-' + (index % 6)"
            >
              <!-- <img src="~@/assets/images/icons/customlist-icon.svg" alt="icon" class="feature-icon" /> -->
              <img
                :src="feature.icon"
                alt=""
                width="13px"
                class="feature-icon"
              />
            </div>

            <div class="feature-details">
              <div class="feature-title-row">
                <span class="feature-title">{{ feature.title }}</span>
              </div>
              <div class="feature-desc">{{ feature.description }}</div>
            </div>
          </div>
        </div>
      </div>
    </transition>
    <modal
    :open="openCopyTenantlist"
      @close="openCopyTenantlist = false"
      :closeOnOutsideClick="true"
     >
      <copy-tenant-values-modal
      :migrateTenantList="migrateTenantList"
      @close="openCopyTenantlist = false"
/>
    </modal>
  </div>
</template>

<script>
import customDropdown from './customDropdown.vue'
import {
  GetAllIndependantTenants,
  MigrateTenantData
} from '@/api/apis/userFlow'
import { alert, success } from '@/plugins/notification'
import Loader from '@/plugins/loader'

import customListIcon from '@/assets/images/icons/customlist-icon.svg'
// import locationIcon from '@/assets/images/icons/storage-location.svg'
// import tagsIcon from '@/assets/images/icons/tags-icon.svg'
// import calenderIcon from '@/assets/images/icons/calendar-icon.svg'
import formIcon from '@/assets/images/icons/form-icon.svg'
// import userGroupIcon from '@/assets/images/icons/user-group-icon.svg'
// import workflowIcon from '@/assets/images/icons/workflow-icon.svg'
import sequenceGeneratorIcon from '@/assets/images/icons/sequence-generator-icon.svg'
import tenantFeatureConfigIcon from '@/assets/images/icons/tenant-feature-configuration.svg'
// import checkListIcon from '@/assets/images/icons/check-list-icon.svg'
import copyTenantValuesModal from './copyTenantValuesModal.vue'
import Modal from '@/components/common/modal.vue'

export default {
  name: 'TenantDataCopy',
  components: {
    customDropdown, copyTenantValuesModal, Modal
  },
  data () {
    return {
      selectedSource: null,
      selectedTarget: null,
      sameTenantSelected: false,
      tenantsList: [],
      tenantPageSize: 20,
      tenantSearchTimer: null,
      tenantPagination: {
        offset: 0,
        searchText: '',
        loading: false,
        hasMore: true
      },
      copyLoading: false,
      features: [
        {
          title: 'Custom List',
          icon: customListIcon,
          description:
            'All custom dropdown lists and their hierarchical values',
          selected: true
        },
        // {
        //   title: 'Storage Locations',
        //   icon: tagsIcon,
        //   description: 'Storage areas used for inventory management',
        //   selected: true
        // },
        // {
        //   title: 'Tags',
        //   icon: locationIcon,
        //   description:
        //     'Reusable labels for classification and filtering across entities',
        //   selected: true
        // },
        // {
        //   title: 'Tenant Calendar',
        //   icon: calenderIcon,
        //   description:
        //     'Reusable labels for classification and filtering across entities',
        //   selected: true
        // },
        {
          title: 'Forms',
          icon: formIcon,
          description: 'Configurable forms used for data capture and workflows',
          selected: true
        },
        // {
        //   title: 'User Groups',
        //   icon: userGroupIcon,
        //   description:
        //     'Logical grouping of users for access and assignment control',
        //   selected: true
        // },
        // {
        //   title: 'Workflow',
        //   icon: workflowIcon,
        //   description: 'Tenant-defined workflows governing process execution',
        //   selected: true
        // },
        {
          title: 'Sequence Generation',
          icon: sequenceGeneratorIcon,
          description: 'Configuration for automatic numbering and identifiers',
          selected: true
        },
        {
          title: 'Tenant Feature Configuration',
          icon: tenantFeatureConfigIcon,
          description:
            'Enablement and customization of tenant-specific features',
          selected: true
        }
        // {
        //   title: 'Checklist',
        //   icon: checkListIcon,
        //   description:
        //     'Standardized checklists used for inspections and task validation',
        //   selected: true
        // }
      ],
      openCopyTenantlist: false,
      migrateTenantList: {}
    }
  },
  computed: {
    selectedFeaturesCount () {
      return this.features.filter((f) => f.selected).length
    },
    canCopy () {
      return Boolean(
        this.selectedSource?.id &&
          this.selectedTarget?.id &&
          !this.sameTenantSelected &&
          !this.copyLoading
      )
    }
  },
  methods: {
    selectSourceTenant (tenant) {
      this.sameTenantSelected = false
      this.selectedSource = tenant

      if (this.isSameTenant(tenant, this.selectedTarget)) {
        this.sameTenantSelected = true
      }
    },
    selectTargetTenant (tenant) {
      this.sameTenantSelected = false
      this.selectedTarget = tenant

      if (this.isSameTenant(this.selectedSource, tenant)) {
        this.sameTenantSelected = true
      }
    },
    isSameTenant (sourceTenant, targetTenant) {
      return Boolean(
        sourceTenant?.id &&
          targetTenant?.id &&
          sourceTenant.id === targetTenant.id
      )
    },
    toggleFeature (feature) {
      // by default select all and don't allow to deselect now (1st iteration)
      // feature.selected = !feature.selected
    },
    selectAll () {
      this.features.forEach((f) => {
        f.selected = true
      })
    },
    clearAll () {
      this.features.forEach((f) => {
        f.selected = false
      })
    },
    loadMoreTenants () {
      this.fetchTenants()
    },
    handleTenantSearch (searchText) {
      if (this.tenantSearchTimer) clearTimeout(this.tenantSearchTimer)
      this.tenantSearchTimer = setTimeout(() => {
        this.tenantPagination.searchText = searchText
        this.fetchTenants(true)
      }, 300)
    },
    async handleCopy () {
      if (!this.canCopy) return

      const loader = new Loader()
      this.copyLoading = true
      loader.show()

      try {
        const data = await MigrateTenantData({
          source_tenant_id: this.selectedSource.id,
          target_tenant_id: this.selectedTarget.id,
          modules: ['ALL']
        })

        // handle the success/error based on backend change
        if (data?.error || data?.message?.message) {
          alert(data.error ?? 'Unable to copy tenant data. Please try again.')
          return
        }

        success('Tenant data copied successfully.')
        this.migrateTenantList = data.message
        this.openCopyTenantlist = true
      } catch (error) {
        alert('Unable to copy tenant data. Please try again.')
      } finally {
        this.copyLoading = false
        loader.hide()
      }
    },
    async fetchTenants (reset = false) {
      const pagination = this.tenantPagination
      if (pagination.loading) return

      if (reset) {
        pagination.offset = 0
        pagination.hasMore = true
        this.tenantsList = []
      }

      if (!pagination.hasMore) return

      pagination.loading = true
      try {
        const response = await GetAllIndependantTenants(
          this.tenantPageSize,
          pagination.offset,
          pagination.searchText
        )
        if (response && response.core_tenants) {
          const tenants = response.core_tenants.map((t) => ({
            id: t.id,
            name: t.company_name
          }))
          this.tenantsList = [...this.tenantsList, ...tenants]
          pagination.offset += tenants.length
          pagination.hasMore = tenants.length === this.tenantPageSize
        }
      } catch (error) {
        console.error('Error fetching tenants', error)
      } finally {
        pagination.loading = false
      }
    },

    // transition for avoiding sudden jump when step-2 part closes
    beforeLeave (el) {
      el.style.height = el.scrollHeight + 'px'
    },
    leave (el) {
      // eslint-disable-next-line no-unused-vars
      const reflow = el.offsetHeight
      requestAnimationFrame(() => {
        el.style.height = '0'
      })
    },
    afterLeave (el) {
      el.style.height = ''
    }
  },
  mounted () {
    this.fetchTenants()
  },
  beforeDestroy () {
    if (this.tenantSearchTimer) clearTimeout(this.tenantSearchTimer)
  }
}
</script>

<style scoped>
.tenant-data-copy {
  padding: 24px;
  max-width: 900px;
  margin: 0 auto;
  font-family: "Nunito Sans", sans-serif;
  color: #334155;
}

.header-section {
  padding-bottom: 14px;
  margin-bottom: 14px;
  border-bottom: 1px solid #e2e8f0;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.review-copy-btn {
  display: flex;
  align-items: center;
  gap: 8px;

  padding: 10px 16px;
  border: none;
  border-radius: 8px;

  background: #e39c45;
  color: #fff;

  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  transition: background-color 0.2s ease;
}

.review-copy-btn:hover:not(:disabled) {
  background: #f4a749;
}

.review-copy-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-title {
  font-size: var(--text-l);
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.page-subtitle {
  font-size: var(--text-s);
  color: #64748b;
  margin: 0;
}

.step-section {
  margin-bottom: 14px;
}

.step-header {
  font-size: var(--text-s);
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 16px;
}

.step-1-content {
  display: flex;
  align-items: center;
  gap: 20px;
}

.tenant-selection-warning {
  display: block;
  margin-top: 10px;
  font-size: var(--text-xs2);
  font-weight: 600;
  color: #c2410c;
}

.tenant-card {
  flex: 1;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  background-color: #fff;
}

.badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: var(--text-xs1);
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
}

.source-badge {
  background-color: #e0f2fe;
  color: #0369a1;
}

.target-badge {
  background-color: #ffedd5;
  color: #c2410c;
}

.custom-select-wrapper {
  position: relative;
}

::v-deep .tenant-dropdown .select-label {
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  padding: 0 14px;
  height: 42px;
  font-size: var(--text-s);
  color: #475569;
  cursor: pointer;
  background: transparent;
}

::v-deep .tenant-dropdown .dropdown-icon {
  right: 14px;
  color: #94a3b8;
}

::v-deep .tenant-dropdown .select-label:focus,
::v-deep .tenant-dropdown .select-label:hover {
  border-color: #94a3b8;
}

.copy-arrow-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.arrow-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
}

.copy-text {
  font-size: 10px;
  font-weight: 600;
  color: #94a3b8;
  letter-spacing: 0.5px;
}

.step-2-header-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 16px;
}
.step-2-header-container .step-header {
  margin-bottom: 0;
}

.step-2-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.action-btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-weight: 500;
}
.action-btn:hover {
  text-decoration: underline;
}

.text-primary {
  color: #3b82f6;
}

.text-secondary {
  color: #64748b;
}

.dot-separator {
  color: #cbd5e1;
}

.selected-count-badge {
  background-color: #f1f5f9;
  color: #64748b;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  margin-left: 8px;
}

.features-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.feature-card {
  display: flex;
  align-items: center;
  padding: 16px;
  border: 0.5px solid #e2e8f0;
  border-radius: 8px;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.feature-card:hover {
  border-color: #cbd5e1;
}

.feature-card.is-selected {
  border-color: #e5a83d;
}

.feature-checkbox {
  width: 20px;
  height: 20px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  margin-right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.feature-checkbox.checked {
  background-color: #e5a83d;
  border-color: #e5a83d;
}

.feature-icon-container {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  flex-shrink: 0;
}

.feature-icon {
  width: 18px;
  height: 18px;
  opacity: 0.7;
}

.icon-bg-0 {
  background-color: #e0e7ff;
}
.icon-bg-1 {
  background-color: #ffedd5;
}
.icon-bg-2 {
  background-color: #dcfce7;
}
.icon-bg-3 {
  background-color: #f3e8ff;
}
.icon-bg-4 {
  background-color: #ffe4e6;
}
.icon-bg-5 {
  background-color: #fef3c7;
}

.feature-details {
  flex: 1;
}

.feature-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.feature-title {
  font-size: 15px;
  font-weight: 500;
  color: #1e293b;
}

.record-badge {
  background-color: #f1f5f9;
  color: #94a3b8;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 12px;
}

.warning-icon {
  display: flex;
  align-items: center;
}

.feature-desc {
  font-size: 13px;
  color: #94a3b8;
}

.feature-arrow {
  margin-left: 16px;
  display: flex;
  align-items: center;
}

.curtain-enter-active,
.curtain-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease, height 0.4s ease;
  overflow: hidden;
}

.curtain-enter,
.curtain-leave-to {
  opacity: 0;
  transform: translateY(-15px) scaleY(0.98);
}

.curtain-enter-to,
.curtain-leave {
  opacity: 1;
  transform: translateY(0) scaleY(1);
}
</style>
